// src/Footer.js

import React, { memo } from 'react';
import { motion } from 'framer-motion';

const CURRENT_YEAR = new Date().getFullYear();

const brandAnim   = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.4 } };
const dividerAnim = { initial: { width: 0 }, whileInView: { width: '100%' }, viewport: { once: true }, transition: { duration: 0.6 } };
const bottomAnim  = { initial: { opacity: 0, y: 12 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.4, delay: 0.15 } };
const hoverUp     = { whileHover: { y: -2 }, transition: { duration: 0.15 } };

// ── QR grid ───────────────────────────────────────────────────────────────────
const QrGridBackground = memo(() => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden opacity-[0.03] dark:opacity-[0.05]">
    <div className="absolute inset-0 grid grid-cols-8" style={{ gridTemplateRows: 'repeat(4, 1fr)' }}>
      {Array.from({ length: 32 }).map((_, i) => (
        <div key={i} className="border border-purple-500"
          style={{ opacity: 0, animation: `qrCellPulse ${3 + (i % 4)}s ease-in-out ${(i * 0.08) % 3}s infinite` }} />
      ))}
    </div>
  </div>
));
QrGridBackground.displayName = 'QrGridBackground';

const PARTICLES = [
  { width: 6, height: 6, top: '20%', left: '5%',  duration: 4,   delay: 0   },
  { width: 7, height: 7, top: '65%', left: '93%', duration: 6,   delay: 1.5 },
];

const ParticlesLayer = memo(() => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0">
    {PARTICLES.map((p, i) => (
      <div key={i} className="absolute rounded-full bg-purple-400/15 dark:bg-purple-400/10"
        style={{ width: p.width, height: p.height, top: p.top, left: p.left,
          animation: `particleFloat ${p.duration}s ease-in-out ${p.delay}s infinite` }} />
    ))}
  </div>
));
ParticlesLayer.displayName = 'ParticlesLayer';

const GlowOrb = memo(() => (
  <div aria-hidden="true"
    className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-purple-500/8 blur-[90px]" />
));
GlowOrb.displayName = 'GlowOrb';

// ── Footer ────────────────────────────────────────────────────────────────────
const Footer = memo(({ onAbout, onPrivacy }) => (
  <footer aria-label="Site footer"
    className="relative bg-white dark:bg-[#0a0a14] border-t border-gray-100 dark:border-purple-500/10 transition-colors duration-500 overflow-hidden"
  >
    <QrGridBackground />
    <ParticlesLayer />
    <GlowOrb />

    <div className="relative z-10 max-w-4xl mx-auto py-10 px-6 sm:px-8">

      {/* Brand */}
      <motion.div {...brandAnim} className="flex flex-col items-center gap-2 mb-8">
        <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
          ⬡ Qronetic
        </span>
        <p className="text-sm text-gray-400 dark:text-gray-500 text-center max-w-xs leading-relaxed">
          Free QR code generator and scanner — fast, private, and works right in your browser.
        </p>

        {/* Page links */}
        <div className="flex items-center gap-4 mt-2">
          <button
            onClick={onAbout}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-200"
          >
            About
          </button>
          <span className="text-gray-300 dark:text-gray-700 text-xs">·</span>
          <button
            onClick={onPrivacy}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-200"
          >
            Privacy Policy
          </button>
        </div>
      </motion.div>

      {/* Divider */}
      <div className="flex justify-center mb-8">
        <motion.div {...dividerAnim}
          className="h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />
      </div>

      {/* Bottom row */}
      <motion.div {...bottomAnim}
        className="flex flex-col sm:flex-row items-center justify-between gap-5"
      >
        <div className="flex flex-col items-center sm:items-start gap-1">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {CURRENT_YEAR} OH Development. All rights reserved.
          </p>
          <a
            href="mailto:qronetic@gmail.com"
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-200"
          >
            ✉ qronetic@gmail.com
          </a>
        </div>

        <ul className="flex items-center gap-3">
          <li>
            <motion.a
              href="https://discord.gg/Wz9E43czjt/"
              target="_blank" rel="noopener noreferrer" aria-label="Discord"
              {...hoverUp}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#1a1a2e] hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-400/40 transition-colors duration-200"
            >
              Discord
            </motion.a>
          </li>
          <li>
            <motion.a
              href="https://github.com/zpshazim/"
              target="_blank" rel="noopener noreferrer" aria-label="GitHub"
              {...hoverUp}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#1a1a2e] hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-400/40 transition-colors duration-200"
            >
              GitHub
            </motion.a>
          </li>
        </ul>
      </motion.div>
    </div>
  </footer>
));

Footer.displayName = 'Footer';
export default Footer;